import dayjs from "dayjs";
import "dayjs/locale/sv";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

export const TIMEZONE_STOCKHOLM = "Europe/Stockholm";
export const TIMEZONE_UTC = "UTC";

dayjs.locale("sv");
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Check if date string includes a time zone indicator
 */
export function hasTimeZone(str: string): boolean {
  // eslint-disable-next-line sonarjs/slow-regex
  return str.match(/.+([\+|\-]\d\d:\d\d|Z)$/) !== null;
}

/**
 * Strip time zone from a date string
 */
export function stripTimeZone(str: string | null | undefined): string | null {
  if (!str) {
    return null;
  }
  return str.replace(/([\+|\-]\d\d:\d\d|Z)$/, "");
}

/**
 * Create a date from a date string overriding the time zone specified
 * with Europe/Stockholm. Useful if, for example, a date has been selected
 * in Contentful and a value of "2025-08-29T00:00:00.000Z" (UTC) has
 * been stored to indicate "the start of that day", but what we really wanted
 * was the start of that day in Stockholm time.
 */
export function toDateInStockholmTimeZone(date: string | null | undefined) {
  if (!date) {
    return null;
  }
  return dayjs.tz(stripTimeZone(date), TIMEZONE_STOCKHOLM).toDate();
}

/**
 * Get the current date
 */
export function nowDate(): Date {
  return new Date();
}

/**
 * Get the start of the day in a given time zone
 */
export function startOfDay(date: Date, timeZone: string): Date {
  return dayjs.tz(date, timeZone).startOf("day").toDate();
}

/**
 * Safely parse a date
 */
export function toDate(x: string | number | Date | null | undefined): Date | null {
  if (!x) {
    return null;
  }
  return new Date(x);
}

/**
 * Get the latest given a list of dates
 */
export function latestDate(dates: Date[]): Date | null {
  let latest: Date | null = null;
  for (const date of dates) {
    if (!latest || date > latest) {
      latest = date;
    }
  }
  return latest;
}

/**
 * Format date according to ISO
 */
export function formatDateISO(date: Date | null | undefined) {
  if (!date) {
    return "";
  }
  return dayjs.tz(date).toISOString();
}

/**
 * Format a date - that is output as human readable string.
 *
 * Prefer usage of any of the formatDateFoo helpers for ease
 * of use and maintainability.
 */
export function formatDate(
  date: Date | null | undefined,
  format: string,
  timeZone: string = TIMEZONE_STOCKHOLM,
): string | null {
  if (!date) {
    return null;
  }
  return dayjs.tz(date, timeZone).format(format);
}

/**
 * Format as day of month only
 */
// eslint-disable-next-line sonarjs/use-type-alias
export function formatDateDay(date: Date | string | null | undefined): string | null {
  return formatDate(toDate(date), "D");
}

/**
 * Format as day of week only
 */
export function formatDateDayOfWeek(date: Date | string | null | undefined): string | null {
  return formatDate(toDate(date), "dddd");
}

/**
 * Format as month only
 */
export function formatDateMonth(date: Date | string | null | undefined): string | null {
  return formatDate(toDate(date), "MMMM");
}

/**
 * Format as month only, limiting number of characters to 3 if month name is longer
 * than 4 characters (to allow full name of june, july, etc).
 */
export function formatDateMonthShort(date: Date | string | null | undefined): string | null {
  const month = formatDate(toDate(date), "MMMM");
  return month?.length > 4 ? month?.substring(0, 3) : month;
}

/**
 * Format as year only
 */
export function formatDateYear(date: Date | string | null | undefined): string | null {
  return formatDate(toDate(date), "YYYY");
}

/**
 * Format as day of month and month
 */
export function formatDateDayMonth(date: Date | string | null | undefined): string | null {
  return formatDate(toDate(date), "D MMMM");
}

/**
 * Format as day of month and month
 */
export function formatDateMonthYear(date: Date | string | null | undefined): string | null {
  return capitalizeFirstLetter(formatDate(toDate(date), "MMMM YYYY"));
}

/**
 * Format as day of month, month and year
 */
export function formatDateDayMonthYear(date: Date | string | null | undefined): string | null {
  return formatDate(toDate(date), "D MMMM YYYY");
}

export function formatDateDayMonthYearAndTime(
  date: Date | string | null | undefined,
): string | null {
  const dateSwe = dayjs.tz(date, "Europe/Stockholm");
  return `${dateSwe.format("D MMMM YYYY [klockan] HH.mm")}`;
}

const capitalizeFirstLetter = (text: string) => {
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/**
 * Get a formatted event date
 */
export const formatEventDate = (startDate: Date, endDate: Date): string => {
  const start = dayjs.tz(startDate, "Europe/Stockholm");
  const end = dayjs.tz(endDate, "Europe/Stockholm");

  return start.isSame(end, "day")
    ? `${capitalizeFirstLetter(start.format("dddd D MMMM YYYY"))} klockan ${start.format(
        "HH.mm",
      )}–${end.format("HH.mm")}`
    : `${capitalizeFirstLetter(start.format("dddd D MMMM YYYY HH.mm"))} – ${end.format(
        "dddd D MMMM YYYY HH.mm",
      )}`;
};

/**
 * Get a formatted event date without year or "klockan"
 */
export const formatShortEventDate = (startDate: Date, endDate: Date): string => {
  const capitalizeFirstLetter = (text: string) => {
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const start = dayjs.tz(startDate, "Europe/Stockholm");
  const end = dayjs.tz(endDate, "Europe/Stockholm");

  return start.isSame(end, "day")
    ? `${capitalizeFirstLetter(start.format("dddd D MMMM"))} ${start.format("HH.mm")}–${end.format(
        "HH.mm",
      )}`
    : `${capitalizeFirstLetter(start.format("dddd D MMMM HH.mm"))} – ${end.format(
        "dddd D MMMM HH.mm",
      )}`;
};

/**
 * Get a press release date formatted
 */
export const formatPressReleaseDate = (publishDate: Date): string => {
  const date = dayjs.tz(publishDate, "Europe/Stockholm");
  return `${date.format("D MMMM YYYY")} klockan ${date.format("HH.mm")}`;
};

/**
 * Get date based on month and year (in Swedish), for example "2024", "Augusti"
 */
export const getDateByYearAndMonth = (year: string | number, month: string): Date => {
  if (!month) {
    return toDateInStockholmTimeZone(String(year));
  }
  return toDateInStockholmTimeZone(`${year}-${getMonthByString(month)}`);
};

/**
 * Get month number from Swedish text representation
 */
const getMonthByString = (month: string): string => {
  switch (month) {
    case "Januari":
      return "01";
    case "Februari":
      return "02";
    case "Mars":
      return "03";
    case "April":
      return "04";
    case "Maj":
      return "05";
    case "Juni":
      return "06";
    case "Juli":
      return "07";
    case "Augusti":
      return "08";
    case "September":
      return "09";
    case "Oktober":
      return "10";
    case "November":
      return "11";
    case "December":
      return "12";
    default:
      return "";
  }
};
